'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { createClient } from '@/lib/supabase/client';

const FALLBACK_LOGO = '/assets/images/Untitled_design-1780921698266.png';
const FALLBACK_HERO = '/assets/images/Untitled_design-1780922354822.png';

interface HeroData {
  logo_url: string | null;
  hero_image_url: string | null;
  event_name: string | null;
  subtitle: string | null;
  event_date: string | null;
  address: string | null;
  venue: string | null;
}

function formatEventDate(dateStr: string | null): string {
  if (!dateStr) return 'Saturday 13 June 2026';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

export default function HeroSection() {
  const [data, setData] = useState<HeroData>({
    logo_url: null,
    hero_image_url: null,
    event_name: 'New Forest Family Fun Day',
    subtitle: 'A fantastic day out for the whole family hosted by New Milton RFC. Gates open at 11:00 AM!',
    event_date: '2026-06-13',
    address: 'Ashley Recreation Ground, Normans Way',
    venue: 'New Milton RFC',
  });

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('event_details')
      .select('logo_url, hero_image_url, event_name, subtitle, event_date, address, venue')
      .limit(1)
      .single()
      .then(({ data: row }) => {
        if (row) setData(row);
      });
  }, []);

  const logoUrl = data.logo_url || FALLBACK_LOGO;
  const heroUrl = data.hero_image_url || FALLBACK_HERO;
  const [titleLine1, titleLine2] = (data.event_name ?? 'New Forest Family Fun Day').split(' ').reduce(
    (acc: [string, string], word, i, arr) => {
      if (i < Math.ceil(arr.length / 2)) acc[0] = (acc[0] ? acc[0] + ' ' : '') + word;
      else acc[1] = (acc[1] ? acc[1] + ' ' : '') + word;
      return acc;
    },
    ['', '']
  );

  return (
    <section className="gradient-hero relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute -top-16 -right-16 w-48 h-48 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, var(--primary) 0%, transparent 70%)' }}
        />
        <div
          className="absolute bottom-0 -left-12 w-36 h-36 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)' }}
        />
      </div>
      <div className="relative px-4 pt-8 pb-10 max-w-lg mx-auto text-center">
        {/* Logo */}
        <div className="flex justify-center mb-4 animate-fade-up">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-3 border border-white/20">
            <Image
              src={logoUrl}
              alt="New Forest Family Fun Day Logo"
              width={160}
              height={160}
              className="rounded-xl"
              priority
              unoptimized={logoUrl?.startsWith('http')}
            />
          </div>
        </div>

        {/* Date badge */}
        <div className="inline-flex items-center gap-2 bg-primary/20 border border-primary/40 rounded-full px-4 py-1.5 mb-4 animate-fade-up">
          <span className="w-2 h-2 rounded-full bg-primary animate-pulse-slow" />
          <span className="text-primary text-xs font-bold tracking-widest uppercase">
            {formatEventDate(data.event_date)}
          </span>
        </div>

        {/* Title */}
        <h1 className="text-hero text-white mb-3 animate-fade-up">
          {titleLine1}<br />
          <span className="text-primary">{titleLine2}</span>
        </h1>

        <p className="text-white/70 text-sm leading-relaxed mb-6 animate-fade-up max-w-xs mx-auto">
          {data.subtitle ?? 'A fantastic day out for the whole family hosted by New Milton RFC. Gates open at 11:00 AM!'}
        </p>

        {/* Hero image */}
        <div className="relative rounded-2xl overflow-hidden card-shadow-lg">
          <Image
            src={heroUrl}
            alt="New Milton Fun Day event photo"
            width={600}
            height={400}
            className="w-full h-48 object-cover"
            unoptimized={heroUrl?.startsWith('http')}
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-secondary/80 to-transparent p-3">
            <p className="text-white text-xs font-semibold">
              {data.address ?? 'Ashley Recreation Ground, Normans Way'}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}